import moment from "moment";
import config from "config/config";
import { Grid } from "gridjs-react";
import { v4 as uuidv4 } from "uuid";
import { useDispatch } from "react-redux";
import { Dialog } from "@headlessui/react";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { getFinancials } from "service/policy/getFinancials";
import {
  isFlagEnabled,
  isPolicyInGrace,
  isOldAlicUlPolicy,
  isWholeLifePolicy,
  showCoverageAmountForBenefit,
  isMITPolicy,
} from "js/utilities";
import {
  getBadgeText,
  getCompanyNameByBlockCode,
  getBadgeClassByPolicyStatus,
  stringFormattingToCurrency,
  filterAndFormatRiderPolicies,
  getBenefitName,
} from "js/formatting";

import { getImage } from "redux/slices/getImageSlice";
import { useCollapsePanel } from "hooks/useCollapsePanel";
import { getCorrespondenceList } from "redux/slices/getCorrespondenceListSlice";

import Badge from "components/General/Badge";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import CardDisclaimerBase from "components/General/CardDisclaimerBase";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import ContactUsDialog from "components/ModalDialogs/ContactUsDialog";

export default function CoverageAndDocumentsCard({ data }) {
  const dispatch = useDispatch();
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPdfLoading, setIsPdfLoading] = useState(true);
  const [pdfSource, setPdfSource] = useState("");
  const [PDFDialog, setPDFDialog] = useState(false);
  const [desc, setDesc] = useState("");
  const { t } = useTranslation();
  const [contactUsDialogState, setContactUsDialogState] = useState(false);
  const [lapsedOn, setLapsedOn] = useState();

  useEffect(() => {
    if (
      !isPolicyInGrace(data) ||
      isOldAlicUlPolicy(data) ||
      data.status?.toLowerCase() === "extended term"
    ) {
      dispatch(
        getCorrespondenceList({
          policyId: [data?.contractID],
          agentId: "",
          beginDate: moment()
            .subtract(parseInt(config.ANNUAL_STATEMENT_DURATION), "year")
            .format("MM/DD/YYYY"),
          endDate: moment().format("MM/DD/YYYY"),
        })
      ).then((response) => {
        if (response?.meta?.requestStatus === "fulfilled") {
          setFilteredDocuments(
            response.payload.allAlerts.filter(
              (x) => x.document.typeOfDocument.toUpperCase() === "ANNSTMT"
            )
          );
        }
      });
      if (data?.status?.toLowerCase() === "lapsed")
        setLapsedOn(moment(data?.statusDate).format("MM/DD/YYYY"));
    } else {
      Promise.all([
        dispatch(
          getCorrespondenceList({
            policyId: [data?.contractID],
            agentId: "",
            beginDate: moment()
              .subtract(parseInt(config.ANNUAL_STATEMENT_DURATION), "year")
              .format("MM/DD/YYYY"),
            endDate: moment().format("MM/DD/YYYY"),
          })
        ),
        getFinancials(data),
      ]).then((res) => {
        let documents = res[0];
        let financials = res[1];
        if (documents?.meta?.requestStatus === "fulfilled") {
          setFilteredDocuments(
            documents.payload.allAlerts.filter(
              (x) => x.document.typeOfDocument.toUpperCase() === "ANNSTMT"
            )
          );
        }
        if (financials) {
          // Sort in descending order
          let cashValues = financials.cashValues?.sort(
            (a, b) =>
              moment(b?.projectedlapseDate) - moment(a?.projectedlapseDate)
          );
          const lapseDate = cashValues[0]?.projectedlapseDate;
          if (lapseDate) setLapsedOn(moment(lapseDate).format("MM/DD/YYYY"));
        }
      });
    }
  }, [data, dispatch]);

  const coverageAndDocumentsObj = {
    oldPolicy: data.oldPolicy,
    contractID: data.contractID,
    faceAmount: data?.cashValue?.currentFaceAmount,
    productName: data?.product?.description,
    modalPremiumAmountTotal: data?.modalPremiumAmountTotal,
    estimatedSurrenderValue: data?.policyFinancials?.estimatedSurrenderValue,
    issueDate: data.effectiveDate
      ? moment(data.effectiveDate).format("MM/DD/YYYY")
      : "",
    maturityDate: data.maturityDate
      ? moment(data.maturityDate).format("MM/DD/YYYY")
      : "",
  };

  const uuid = uuidv4();
  useCollapsePanel(uuid);

  return (
    <>
      <div className="ui-card mb-4 xl:mb-6">
        <div className="md:flex md:flex-row md:items-start">
          <div className="px-4 pt-3 xl:px-6 xl:pt-6">
            <h2 className="mb-0">
              <button
                id={uuid}
                className="flex items-center group md:hidden"
                aria-controls={`${uuid}-panel`}
                aria-expanded="true"
                aria-label="Expand or collapse coverage &amp; documents details"
              >
                <span className="inline-block p-1 rounded-full mr-2 text-ui-blue group-hover:text-white group-hover:bg-ui-blue">
                  <svg
                    className="ui-icon ui-plus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-plus"></use>
                  </svg>
                  <svg
                    className="ui-icon ui-minus w-4 h-4"
                    focusable="false"
                    aria-hidden="true"
                    role="presentation"
                  >
                    <use href="#ui-icon-minus"></use>
                  </svg>
                </span>
                <span
                  className={`text-base font-body font-bold md:ui-no-badge-icon-inline ${
                    getBadgeClassByPolicyStatus(data)
                      ? `ui-badge-inline-icon-${getBadgeClassByPolicyStatus(
                          data
                        )}`
                      : ""
                  }`}
                >
                  {t("CustomerPolicyDetails-coverage-documents")}
                </span>
              </button>

              <span className="text-xl font-body font-bold hidden md:block">
                {t("CustomerPolicyDetails-coverage-documents")}
              </span>
            </h2>
          </div>
          <div className="px-4 pt-3 pr-6 xl:px-6 xl:pt-6 md:ml-auto">
            {/* <!-- there is an associated mobile class "ui-badge-inline-icon-problem" on the title that displays when this block hides --> */}
            <div className="hidden md:block">
              {data?.status && data?.status !== "" && (
                <Badge
                  status={getBadgeClassByPolicyStatus(data)}
                  statusText={getBadgeText(data)}
                  srPrefix="Policy"
                />
              )}
            </div>
          </div>
        </div>
        <hr className="hidden md:block" />

        <div
          id={`${uuid}-panel`}
          className="px-4 pb-4 overflow-hidden h-20 md:h-auto xl:px-6 xl:pb-6"
        >
          {/* <!-- Policy Details --> */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:gap-6">
            {config.ENABLE_SHOW_COMPANY_NAME_ON_ANNUITY_POLICIES === "true" && (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("CustomerPolicyDetails-coverage-documents-company")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {getCompanyNameByBlockCode(data?.blockCode)}
                </div>
              </div>
            )}
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {t("CustomerPolicyDetails-coverage-documents-policy-number")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {coverageAndDocumentsObj?.oldPolicy}
              </div>
            </div>
            {!isMITPolicy(data) && (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("CustomerPolicyDetails-coverage-documents-coverage")}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {stringFormattingToCurrency(
                    coverageAndDocumentsObj?.faceAmount
                  )}
                </div>
              </div>
            )}
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {t("CustomerPolicyDetails-coverage-documents-product-name")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {coverageAndDocumentsObj?.productName}
              </div>
            </div>
            <div>
              <div className="text-xs text-theme-text-tertiary">
                {t("CustomerPolicyDetails-coverage-documents-issue-date")}
              </div>
              <div className="text-sm font-semibold text-theme-text-secondary">
                {coverageAndDocumentsObj?.issueDate}
              </div>
            </div>
            {isOldAlicUlPolicy(data) &&
              data?.status?.toLowerCase() === "lapsed" && (
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t("CustomerPolicyDetails-coverage-documents-lapsed-date", {
                      value: "Lapsed",
                    })}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {data?.statusDate}
                  </div>
                </div>
              )}
            {!isOldAlicUlPolicy(data) && lapsedOn && (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t("CustomerPolicyDetails-coverage-documents-lapsed-date", {
                    value:
                      data?.status?.toLowerCase() === "lapsed"
                        ? "Lapsed"
                        : "Lapse",
                  })}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {lapsedOn}
                </div>
              </div>
            )}
            {isWholeLifePolicy(data?.product?.type) ||
              (isOldAlicUlPolicy(data) && (
                <div>
                  <div className="text-xs text-theme-text-tertiary">
                    {t(
                      "CustomerPolicyDetails-coverage-documents-maturity-date"
                    )}
                  </div>
                  <div className="text-sm font-semibold text-theme-text-secondary">
                    {coverageAndDocumentsObj?.maturityDate}
                  </div>
                </div>
              ))}
            {isOldAlicUlPolicy(data) && (
              <div>
                <div className="text-xs text-theme-text-tertiary">
                  {t(
                    "CustomerPolicyDetails-coverage-documents-death-benefit-option"
                  )}
                </div>
                <div className="text-sm font-semibold text-theme-text-secondary">
                  {t(
                    "CustomerPolicyDetails-coverage-documents-death-benefit-option-value",
                    {
                      value: data?.deathBenefit?.currentDeathBenefitOption,
                    }
                  )}
                </div>
              </div>
            )}
          </div>
          {isFlagEnabled("ENABLE_ADDITIONAL_BENEFITS_SECTION") &&
            data?.supplementaryBenefitAndCharges?.length > 0 &&
            filterAndFormatRiderPolicies(data?.supplementaryBenefitAndCharges)
              ?.length > 0 && (
              <>
                <div className="font-semibold mt-6 mb-1">
                  {t(
                    "CustomerPolicyDetails-coverage-documents-additional-benefits"
                  )}
                </div>
                <Grid
                  columns={[
                    {
                      id: "Type",
                      data: (row) =>
                        row?.isRider ? row.type : getBenefitName(row.type),
                    },
                    {
                      id: "Amount",
                      data: (row) =>
                        row?.isRider
                          ? stringFormattingToCurrency(row.benefit)
                          : !showCoverageAmountForBenefit(row.type)
                          ? stringFormattingToCurrency(row.benefit)
                          : "",
                    },
                    ...(isFlagEnabled(
                      "SHOW_ISSUED_DATE_IN_ADDITIONAL_BENEFITS_SECTION"
                    )
                      ? [
                          {
                            id: "Date Issued",
                            name: "Date Issued",
                            data: (row) => `${row.beginDate}`,
                          },
                        ]
                      : []),
                    ...(isFlagEnabled(
                      "SHOW_EXPIRY_DATE_IN_ADDITIONAL_BENEFITS_SECTION"
                    )
                      ? [
                          ...(!isWholeLifePolicy(data?.product?.type)
                            ? [
                                {
                                  id: "Expiry Date",
                                  name: "Expiry Date",
                                  data: (row) => `${row.expiryDate}`,
                                },
                              ]
                            : []),
                        ]
                      : []),
                  ]}
                  data={filterAndFormatRiderPolicies(
                    data?.supplementaryBenefitAndCharges
                  )}
                  className={{
                    table: "ui-grid-table w-full",
                  }}
                />
              </>
            )}

          <EverlakeLoadingOverlay isLoading={loading}>
            {filteredDocuments?.length > 0 && (
              <>
                <div className="font-semibold mt-6 mb-1">
                  {t(
                    "CustomerPolicyDetails-coverage-documents-pdf-preview-policy-documents"
                  )}
                </div>
                <div className="ui-download-document">
                  {filteredDocuments?.map((document, index) => {
                    if (
                      document?.document.typeOfDocument.toUpperCase() ===
                      "ANNSTMT"
                    ) {
                      return (
                        <div
                          className="inline-block p-2 border border-ui-gray-light-medium rounded"
                          key={`document${index}`}
                        >
                          <div className="text-xs font-semibold text-theme-text-secondary whitespace-nowrap">
                            {t(
                              "CustomerPolicyDetails-coverage-documents-annual-statement"
                            )}{" "}
                            {moment(document?.document?.dateOfDocument).year()}
                          </div>
                          <div>
                            <button
                              className="ui-link-icon font-bold text-xs"
                              onClick={() => {
                                setLoading(true);
                                dispatch(
                                  getImage({
                                    imageFileName: document.document.name,
                                    policyId: document.policy.contractID,
                                  })
                                ).then((response) => {
                                  setLoading(false);
                                  setPDFDialog(true);
                                  setIsPdfLoading(false);
                                  setDesc(document.document.description);
                                  setPdfSource(
                                    response.payload.document.contentStream.type
                                  );
                                });
                              }}
                            >
                              <svg
                                className="ui-icon w-4 h-4"
                                focusable="false"
                                aria-hidden="true"
                                role="presentation"
                              >
                                <use href="#ui-icon-document-download"></use>
                              </svg>
                              {t(
                                "CustomerPolicyDetails-coverage-documents-download-pdf"
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              </>
            )}
          </EverlakeLoadingOverlay>
        </div>

        <CardDisclaimerBase
          disclaimer={
            <>
              {t("CustomerPolicyDetails-coverage-documents-disclaimer")}
              <br />
              <div className="pt-1">
                <Trans
                  i18nKey={
                    "CustomerPolicyDetails-coverage-documents-annualstmt-disclaimer"
                  }
                  components={{
                    button1: (
                      <button
                        onClick={() => setContactUsDialogState(true)}
                        className="ui-btn-hyperlink text-xs font-semibold ml-0"
                      ></button>
                    ),
                  }}
                />{" "}
              </div>
            </>
          }
        />
        <ContactUsDialog
          dialogState={contactUsDialogState}
          setDialogState={setContactUsDialogState}
        />
      </div>

      <Dialog
        open={PDFDialog}
        onClose={() => setPDFDialog(false)}
        className="ui-dialogs"
      >
        <DialogBackdrop />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            role="dialog"
            aria-modal="true"
            className="ui-default-dialog w-4/5 h-[90%]"
          >
            <Dialog.Title className="font-body text-xl font-bold text-left">
              {t("CustomerPolicyDetails-coverage-documents-pdf-preview")}
            </Dialog.Title>
            <button
              className="ui-default-dialog-close"
              aria-label="close manage notification preferences window"
              onClick={() => setPDFDialog(false)}
            >
              <svg
                className="ui-icon h-3.5 w-3.5"
                focusable="false"
                aria-hidden="true"
                role="presentation"
              >
                <use href="#ui-icon-close"></use>
              </svg>
            </button>
            <hr />
            <Dialog.Description>{desc}</Dialog.Description>

            {isPdfLoading ? (
              <p>
                {t(
                  "CustomerPolicyDetails-coverage-documents-pdf-preview-message"
                )}
              </p>
            ) : (
              <embed
                className="w-full h-[80%]"
                src={`data:application/pdf;base64, ${pdfSource}`}
              />
            )}
            <hr />
            <div className="block text-right mt-6">
              <button
                className="ui-btn-secondary min-w-[140px] mt-4"
                aria-label="Close window"
                onClick={() => setPDFDialog(false)}
              >
                {t(
                  "CustomerPolicyDetails-coverage-documents-pdf-preview-close"
                )}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
}
